import { useAsyncData } from '@snapchat/async-data-browser';
import { htmlEscape, htmlUnescape } from '@snapchat/core';
import { type FC, useContext, useState } from 'react';

import { ConsumerContext } from '../../../../components/ConsumerContextProvider';
import { Feature, useFeatureFlags } from '../../../../components/FeatureFlags';
import { Config } from '../../../../config';
import { logger } from '../../../../helpers/logging';
import { customFetch } from '../../../../utils/fetch/customFetch';
import { filterFieldMap } from './constants';
import { JobPostings } from './JobPostings';
import type { ElasticJobData, JobQuerySearchHit, ModifiedJobData } from './types';

/**
 * Groups jobs payload into buckets by role since we use seperate tables per job role we also
 * transform the data to be more easily rendered
 *
 * @param data Unbucketed jobs
 * @param careersEnableJobDetailPage Boolean
 * @returns Record<string, ModifiedJobData[]>
 */
const bucketJobs = (
  data: JobQuerySearchHit[],
  careersEnableJobDetailPage: boolean
): Record<string, ModifiedJobData[]> => {
  const bucketedData = data.reduce(
    (acc: Record<string, ModifiedJobData[]>, currentSearchHit: JobQuerySearchHit) => {
      const currentJob = currentSearchHit._source;

      if (!acc[currentJob.role]) {
        acc[currentJob.role] = [];
      }

      const currentJobUrl = careersEnableJobDetailPage
        ? `job?id=${currentJob.id}`
        : currentJob.absolute_url;

      acc[currentJob.role]?.push({
        //TODO: modify role field to better match data
        role: currentJob.departments ?? '',
        employment_type: currentJob.employment_type,
        primary_location: currentJob.primary_location,
        title: { text: currentJob.title, url: currentJobUrl },
        locations: currentJob.offices
          ? currentJob.offices.map(office => office.name).join('; ')
          : currentJob.primary_location,
        // Include search results (if any) for title and job description for highlighting
        descriptionSearchResult: currentSearchHit.highlight?.jobDescription?.[0],
        titleSearchResult: currentSearchHit.highlight?.title?.[0],
      });

      return acc;
    },
    {}
  );

  return bucketedData;
};

const filterKeys = Object.keys(filterFieldMap);

export const JobPostingsShallow: FC = () => {
  const featureFlags = useFeatureFlags();
  const careersEnableJobDetailPage =
    featureFlags[Feature.CAREERS_ENABLE_JOB_DETAIL_PAGE] === 'true';

  const { getUrlParams } = useContext(ConsumerContext);
  const [query, setQuery] = useState(new URLSearchParams(getUrlParams?.() ?? {}));
  const filters = Object.fromEntries(filterKeys.map(key => [key, query.get(key) ?? '']));

  const searchQuery = query.get('q');

  const filterKey = `jobs-${query}-${htmlEscape(JSON.stringify(filters))}`;

  const { data, isLoading } = useAsyncData({
    // TODO: Update to use some hashing function instead of JSON.stringify
    dataId: filterKey,
    dataAsync: async () => {
      const port = process.env.PORT ?? 3000;
      const host = Config.isClient ? '' : `http://localhost:${port}`;
      const params = new URLSearchParams();

      const filterEntries = Object.entries(filters);

      for (const [key, value] of filterEntries) {
        params.set(key, htmlUnescape(htmlEscape(value)));
      }

      if (searchQuery) {
        params.set('q', htmlEscape(searchQuery));
      }

      const url =
        filterEntries.length || searchQuery
          ? `${host}/api/jobs?${params.toString()}`
          : `${host}/api/jobs`;

      const res = await customFetch(url);
      const data: ElasticJobData = await res.json();

      return data;
    },
    onError: error => logger.logError({ error, component: 'JobsPostings' }),
  });

  const hasSearchQuery = Boolean(searchQuery?.trim());

  return (
    <JobPostings
      isLoading={isLoading}
      filters={filters}
      jobs={bucketJobs(data?.body ?? [], careersEnableJobDetailPage)}
      filterValues={data?.aggs ?? {}}
      setQuery={setQuery}
      filterKey={filterKey}
      sortJobGroupsAlphabetically={!hasSearchQuery}
    />
  );
};
